import snakecaseKeys from 'snakecase-keys';

import { restClient } from '../../client';


export enum AgreementTermType {
  days = 'days',
  months = 'months',
  years = 'years',
}

export enum AgreementTemplateType {
  none = 'none',
  collaborativePracticeAgreement = 'collaborative_practice_agreement',
}

export type AgreementTemplate = {
  id: string;
  type: AgreementTemplateType;
  name: string;
  active: boolean;
  description: string;
  stateIds: string[];
  version: number | null;
  versionUpdatedAt: string;
  agreementTermType: AgreementTermType;
  agreementTermValue: number;
  renewalReminderLeadDays: number;
  gracePeriodAfterExpirationDays: number;
  reminderFrequencyDaysDuringGracePeriod: number;
  requireBothPartiesToResignOnRenewal: boolean;
  allowPdfUploadAsFallbackToPlatformSigning: boolean;
  createdAt: string;
  updatedAt: string | null;
};


export type AgreementTemplateVersion = {
  templateId: string;
  version: number;
  content: string;
  createdAt: string;
  updatedAt: string;
};


export const getAllAgreementTemplates = () => restClient.request<{ data: AgreementTemplate[] }>(
  '/api/v1/admin/agreement-template'
);


export const getAgreementTemplatesByIds = (ids: string[]) => restClient.request<{ data: AgreementTemplate[] }>(
  '/api/v1/agreement-template/by_ids',
  {
    method: 'POST',
    data: { ids: ids },
  }
);


export const getAgreementTemplatePreview = (templateId: string) => restClient.request<{
  data: {
    template: AgreementTemplate;
    version: AgreementTemplateVersion;
  };
}>(
  `/api/v1/agreement-template/${templateId}`
);


export const getAgreementTemplateVersions = (templateId: string) => restClient.request<{ data: AgreementTemplateVersion[] }>(
  `/api/v1/admin/agreement-template/${templateId}/versions`
);


export const createAgreementTemplate = (payload: { name: string }) =>
  restClient.request<{ data: string }>('/api/v1/admin/agreement-template', {
    method: 'POST',
    data: snakecaseKeys(payload),
  });


export type AgreementTemplateMutablePayload = {
  type: AgreementTemplateType;
  name: string;
  description: string;
  stateIds: string[];
  agreementTermType: AgreementTermType;
  agreementTermValue: number;
  renewalReminderLeadDays: number;
  gracePeriodAfterExpirationDays: number;
  reminderFrequencyDaysDuringGracePeriod: number;
  requireBothPartiesToResignOnRenewal: boolean;
  allowPdfUploadAsFallbackToPlatformSigning: boolean;
};

export const updateAgreementTemplate = (templateId: string, payload: AgreementTemplateMutablePayload) =>
  restClient.request<{ data: AgreementTemplate }>(`/api/v1/admin/agreement-template/${templateId}`, {
    method: 'PUT',
    data: snakecaseKeys(payload),
  });


export const createAgreementTemplateVersion = (templateId: string, content: string) =>
  restClient.request<{ data: number }>(`/api/v1/admin/agreement-template/${templateId}/versions`, {
    method: 'POST',
    data: { content },
  });

export const updateAgreementTemplateVersionContent = (templateId: string, version: number, content: string) =>
  restClient.request(`/api/v1/admin/agreement-template/${templateId}/versions/${version}`, {
    method: 'PUT',
    data: { content },
  });


export const publishAgreementTemplateVersion = (templateId: string, version: number) =>
  restClient.request<{ data: null }>(`/api/v1/admin/agreement-template/${templateId}/versions/${version}/publish`, {
    method: 'POST',
  });
